import React from 'react';
import { useTranslation } from 'react-i18next';
import * as Dialog from '@radix-ui/react-dialog';
import { X, AlertTriangle, Info } from 'lucide-react';

/**
 * Shared confirmation modal shell — handles all the boilerplate UI plus
 * WCAG keyboard semantics via `@radix-ui/react-dialog`:
 *   - ESC closes the dialog (unless `isConfirming`)
 *   - Focus is trapped inside the card
 *   - Focus returns to the trigger that opened it
 *   - Root carries `role="alertdialog"` + `aria-modal="true"` automatically
 *
 * Individual modals own their async logic and pass in the relevant props.
 *
 * size='md' → rounded-xl, compact padding (DeleteNote / DeleteGroup / DeleteSession style)
 * size='lg' → rounded-xl, generous padding (DeleteUser / DeleteAccount style)
 */
export interface ConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Modal heading */
  title: string;
  /** Inline error message shown above the body */
  error?: string | null;
  /** Body content — warning text, banners, etc. */
  children: React.ReactNode;
  /** Label for the confirm button (default: "Delete") */
  confirmLabel?: string;
  /** Label shown while `isConfirming` is true (default: "Deleting...") */
  confirmingLabel?: string;
  /** Optional icon rendered left of the confirmLabel text */
  confirmIcon?: React.ReactNode;
  /** When true, the confirm button shows a loading state and all controls are disabled */
  isConfirming: boolean;
  onConfirm: () => void;
  /** Visual size variant */
  size?: 'md' | 'lg';
  /** CSS z-index for the overlay (default: 160) */
  zIndex?: number;
}

export const ConfirmModal: React.FC<ConfirmModalProps> = ({
  isOpen,
  onClose,
  title,
  error,
  children,
  confirmLabel,
  confirmingLabel,
  confirmIcon,
  isConfirming,
  onConfirm,
  size = 'md',
  zIndex = 160,
}) => {
  const { t } = useTranslation('common');
  const resolvedConfirmLabel = confirmLabel ?? t('common.delete');
  const resolvedConfirmingLabel = confirmingLabel ?? t('common.deleting');

  const isMd = size === 'md';

  const handleOpenChange = (open: boolean) => {
    if (open) return;
    // Block close attempts while the confirm action is still in flight.
    if (isConfirming) return;
    onClose();
  };

  return (
    <Dialog.Root open={isOpen} onOpenChange={handleOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay
          className="fixed inset-0 bg-black/70 backdrop-blur-sm animate-in fade-in duration-300"
          style={{ zIndex: zIndex - 1 }}
        />
        <Dialog.Content
          role="alertdialog"
          aria-modal="true"
          aria-describedby={error ? 'confirm-modal-error' : undefined}
          onEscapeKeyDown={(event) => {
            if (isConfirming) event.preventDefault();
          }}
          onPointerDownOutside={(event) => {
            if (isConfirming) event.preventDefault();
          }}
          onInteractOutside={(event) => {
            if (isConfirming) event.preventDefault();
          }}
          className="fixed left-1/2 top-1/2 w-[min(100%,calc(100vw-2rem))] max-w-md -translate-x-1/2 -translate-y-1/2 overflow-hidden modal-surface border border-border bg-card shadow-modal animate-in fade-in zoom-in-95 duration-200 focus:outline-none"
          style={{ zIndex }}
        >
          {/* Header */}
          <div
            className={
              isMd
                ? 'flex items-center justify-between px-8 py-6 border-b border-border-muted'
                : 'flex items-center justify-between px-10 pt-10 pb-5'
            }
          >
            <div className="flex items-center gap-3">
              <div
                className={
                  isMd
                    ? 'p-2 bg-red-500/10 rounded-lg'
                    : 'w-12 h-12 bg-red-500/10 rounded-lg flex items-center justify-center'
                }
              >
                <AlertTriangle size={isMd ? 20 : 24} className="text-red-500" aria-hidden />
              </div>
              <Dialog.Title
                className={
                  isMd
                    ? 'text-xl font-bold text-foreground italic'
                    : 'text-2xl font-black text-foreground tracking-tight'
                }
              >
                {title}
              </Dialog.Title>
            </div>
            <Dialog.Close asChild>
              <button
                type="button"
                disabled={isConfirming}
                aria-label={t('common.close')}
                className={`p-2 text-muted-foreground hover:text-foreground hover:bg-muted/50 rounded-lg transition-all disabled:opacity-50${
                  isMd ? '' : ' -mt-8'
                }`}
              >
                <X size={20} />
              </button>
            </Dialog.Close>
          </div>

          {/* Body */}
          <div className={isMd ? 'p-8 space-y-6' : 'px-10 pb-10 space-y-8'}>
            {/* Error banner */}
            {error && (
              <div
                id="confirm-modal-error"
                role="alert"
                className={
                  isMd
                    ? 'p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-xs flex items-start gap-2'
                    : 'p-4 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-xs flex items-start gap-3 animate-in slide-in-from-top-2'
                }
              >
                <Info size={isMd ? 14 : 16} className="mt-0.5 shrink-0" aria-hidden />
                <span className="font-medium leading-relaxed">{error}</span>
              </div>
            )}

            {/* Caller-supplied body */}
            {children}

            {/* Action buttons */}
            <div className={isMd ? 'flex items-center justify-end gap-3 pt-4' : 'flex items-center justify-end gap-4 pt-2'}>
              <Dialog.Close asChild>
                <button
                  type="button"
                  disabled={isConfirming}
                  className={
                    isMd
                      ? 'px-6 py-2.5 text-sm font-bold text-muted-foreground hover:text-foreground hover:bg-muted border border-border rounded-lg transition-all disabled:opacity-50'
                      : 'px-6 py-3 text-sm font-bold text-muted-foreground hover:text-foreground hover:bg-muted border border-border rounded-lg transition-all active:scale-95 disabled:opacity-50'
                  }
                >
                  {t('common.cancel')}
                </button>
              </Dialog.Close>
              <button
                type="button"
                onClick={onConfirm}
                disabled={isConfirming}
                className={
                  isMd
                    ? 'px-8 py-2.5 bg-red-500 hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold rounded-lg text-sm active:scale-95 transition-all flex items-center justify-center gap-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2'
                    : 'px-8 py-3 bg-red-500 hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold rounded-lg text-sm active:scale-95 transition-all flex items-center justify-center gap-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2'
                }
              >
                {isConfirming ? (
                  resolvedConfirmingLabel
                ) : (
                  <>
                    {confirmIcon}
                    {resolvedConfirmLabel}
                  </>
                )}
              </button>
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
